// Customers data access. The ONLY place that talks to the `customers` and
// `customer_allocations` tables. Pure mappers (unit-tested) + CRUD.
//
// Note: a customer + its allocations are written across multiple statements
// (not a single transaction). That's acceptable for now and RLS-safe; if we need
// atomicity later we'll move it to a Postgres function (see docs/HARDENING.md).

import { supabase } from "../../lib/supabase";
import { today } from "../../lib/format";
import type { Customer, CustomerAllocation, CustomerInput } from "../../lib/types";

interface CustomerRow {
  id: string;
  account_id: string;
  code: string | null;
  name: string;
  tier: number;
  daily_base: number;
  growth: number;
  onboarding_date: string | null;
  dow_profile: string;
  custom_dow_factors: number[] | null;
  calibration_weeks: number;
  last_calibrated_at: string | null;
  active: boolean;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
}

interface AllocationRow {
  id: string;
  account_id: string;
  customer_id: string;
  hub_id: string;
  percentage: number;
  effective_date: string | null;
  end_date: string | null;
}

/** DB allocation row -> app shape. */
export function rowToAllocation(row: AllocationRow): CustomerAllocation {
  return {
    hubId: row.hub_id,
    percentage: Number(row.percentage),
    effectiveDate: row.effective_date ?? undefined,
    endDate: row.end_date ?? undefined,
  };
}

/** DB customer row (+ its allocation rows) -> app Customer. */
export function rowToCustomer(row: CustomerRow, allocations: AllocationRow[]): Customer {
  return {
    id: row.id,
    code: row.code ?? undefined,
    name: row.name,
    tier: row.tier,
    dailyBase: Number(row.daily_base),
    growth: Number(row.growth),
    onboardingDate: row.onboarding_date ?? undefined,
    dowProfile: row.dow_profile,
    customDowFactors: Array.isArray(row.custom_dow_factors) ? row.custom_dow_factors : undefined,
    calibrationWeeks: Number(row.calibration_weeks ?? 3),
    lastCalibratedAt: row.last_calibrated_at ?? undefined,
    active: row.active,
    contactName: row.contact_name ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    notes: row.notes ?? undefined,
    allocations: allocations
      .filter((a) => a.customer_id === row.id)
      .map(rowToAllocation),
  };
}

/** App CustomerInput -> DB customer row fields (allocations handled separately). */
export function customerInputToRow(accountId: string, input: CustomerInput) {
  return {
    account_id: accountId,
    code: input.code?.trim() || null,
    name: input.name.trim(),
    tier: input.tier,
    daily_base: input.dailyBase,
    growth: input.growth,
    onboarding_date: input.onboardingDate || null,
    dow_profile: input.dowProfile,
    custom_dow_factors: input.dowProfile === "custom" ? (input.customDowFactors ?? null) : null,
    calibration_weeks: input.calibrationWeeks ?? 3,
    last_calibrated_at: input.lastCalibratedAt || null,
    active: input.active,
    contact_name: input.contactName?.trim() || null,
    email: input.email?.trim() || null,
    phone: input.phone?.trim() || null,
    notes: input.notes?.trim() || null,
  };
}

/** App allocation -> DB allocation row fields. */
export function allocationInputToRow(
  accountId: string,
  customerId: string,
  alloc: CustomerAllocation
) {
  return {
    account_id: accountId,
    customer_id: customerId,
    hub_id: alloc.hubId,
    percentage: alloc.percentage,
    effective_date: alloc.effectiveDate || null,
    end_date: alloc.endDate || null,
  };
}

/**
 * Apply a calibration to the customer-level forecast level: set the daily base +
 * the calibration interval, stamping last_calibrated_at. DOW factors are NOT set
 * here — they live per (customer, hub) lane (see saveLaneDowFactors). Scoped by
 * id + account.
 */
export async function setCustomerBase(
  accountId: string,
  id: string,
  fields: { dailyBase: number; calibrationWeeks: number }
): Promise<void> {
  const { error } = await supabase
    .from("customers")
    .update({
      daily_base: Math.round(fields.dailyBase),
      calibration_weeks: fields.calibrationWeeks,
      last_calibrated_at: today(),
    })
    .eq("id", id)
    .eq("account_id", accountId);
  if (error) throw error;
}

/** List all customers for an account (with their allocations), ordered by name. */
export async function listCustomers(accountId: string): Promise<Customer[]> {
  const [{ data: custs, error: e1 }, { data: allocs, error: e2 }, { data: lanes, error: e3 }] =
    await Promise.all([
      supabase.from("customers").select("*").eq("account_id", accountId).order("name"),
      supabase.from("customer_allocations").select("*").eq("account_id", accountId),
      supabase.from("customer_lanes").select("customer_id, hub_id, dow_factors").eq("account_id", accountId),
    ]);
  if (e1) throw e1;
  if (e2) throw e2;
  if (e3) throw e3;
  const allocRows = (allocs ?? []) as AllocationRow[];

  // Per-hub DOW factors keyed by customer, attached as a read-derived field.
  const laneDow = new Map<string, Record<string, number[]>>();
  for (const l of (lanes ?? []) as Array<{ customer_id: string; hub_id: string; dow_factors: unknown }>) {
    if (!Array.isArray(l.dow_factors)) continue;
    const m = laneDow.get(l.customer_id) ?? {};
    m[l.hub_id] = l.dow_factors as number[];
    laneDow.set(l.customer_id, m);
  }

  return (custs as CustomerRow[]).map((c) => ({
    ...rowToCustomer(c, allocRows),
    laneDowFactors: laneDow.get(c.id),
  }));
}

/** Write the allocations for a customer (used by create + update). */
async function writeAllocations(
  accountId: string,
  customerId: string,
  allocations: CustomerAllocation[]
): Promise<void> {
  if (allocations.length === 0) return;
  const rows = allocations.map((a) => allocationInputToRow(accountId, customerId, a));
  const { error } = await supabase.from("customer_allocations").insert(rows);
  if (error) throw error;
}

/** Create a customer (+ allocations) and return it. */
export async function createCustomer(accountId: string, input: CustomerInput): Promise<Customer> {
  const { data, error } = await supabase
    .from("customers")
    .insert(customerInputToRow(accountId, input))
    .select("*")
    .single();
  if (error) throw error;
  const row = data as CustomerRow;
  await writeAllocations(accountId, row.id, input.allocations);
  return { ...rowToCustomer(row, []), allocations: input.allocations };
}

/** Update a customer (+ replace its allocations). Scoped by id AND account_id. */
export async function updateCustomer(
  accountId: string,
  id: string,
  input: CustomerInput
): Promise<Customer> {
  const { data, error } = await supabase
    .from("customers")
    .update(customerInputToRow(accountId, input))
    .eq("id", id)
    .eq("account_id", accountId)
    .select("*")
    .single();
  if (error) throw error;

  // Replace allocations wholesale (simple + correct for the form).
  const { error: delErr } = await supabase
    .from("customer_allocations")
    .delete()
    .eq("customer_id", id)
    .eq("account_id", accountId);
  if (delErr) throw delErr;
  await writeAllocations(accountId, id, input.allocations);

  return { ...rowToCustomer(data as CustomerRow, []), allocations: input.allocations };
}

/**
 * Set a customer's active flag (our soft-delete / pause). Scoped by id AND
 * account_id. We never hard-delete customers from the UI.
 */
export async function setCustomerActive(
  accountId: string,
  id: string,
  active: boolean
): Promise<void> {
  const { error } = await supabase
    .from("customers")
    .update({ active })
    .eq("id", id)
    .eq("account_id", accountId);
  if (error) throw error;
}
